






































































import Vue from 'vue';
import createFocusTrap from 'focus-trap';
import SDialogHeader from '../SDialog/SDialogHeader/SDialogHeader.vue';

const { documentElement } = document;

/**
 * The modal itself and page overlay
 *
 * You probably want to use the SDialog component which wraps this with an activator button.
 */
export default Vue.extend({
  name: 'SPopup',
  components: {
    SDialogHeader,
  },
  props: {
    /**
     * If the component should be visible
     */
    value: {
      type: Boolean,
      required: true,
    },
    /**
     * If true the user won't be able to close the popup
     * It removes the close button and shakes the popup when they click outside
     */
    persistent: {
      type: Boolean,
      default: false,
    },
    /**
     * Stop the modal closing when you click outside of it.
     * Helpful for when the popup has a form inside it
     */
    disableClickOutside: {
      type: Boolean,
      default: false,
    },
    /**
     * The max-width of the dialog or 'auto'
     */
    width: {
      type: [String, Number],
      default: 700,
    },
    /**
     * @see src/components/SDialog/SDialogHeader/SDialogHeader.vue#title
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * @see src/components/SDialog/SDialogHeader/SDialogHeader.vue#icon
     */
    icon: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      wiggleClass: false,
      hasDetached: false,

      mouseDownInsideDialog: false, // If the last mouse down event was inside the dialog
    };
  },
  computed: {
    dialogStyles() {
      const returnStyles: any = {};

      if (this.width === 'auto') {
        returnStyles.width = 'auto';
      } else if (typeof this.width === 'number') {
        returnStyles['max-width'] = `${this.width}px`;
      }

      return returnStyles;
    },
  },
  created() {
    window.addEventListener('keydown', this.windowKeyDown);
    window.addEventListener('animationend', this.dialogAnimationEnd);
  },
  mounted() {
    const self: any = this;
    /**
     * The below is used to move the dialog component to another element so position fixed
     * works properly
     * @see https://github.com/vuetifyjs/vuetify/blob/master/packages/vuetify/src/mixins/detachable.js
     */
    /* eslint-disable no-underscore-dangle */
    if (self._isDestroyed || !this.$refs.content || this.hasDetached) {
      return;
    }
    /* eslint-enable no-underscore-dangle */

    const popupElement = this.$refs.content as Element;
    const target = popupElement.closest('[data-app]');
    if (target == null) {
      return;
    }
    target.insertBefore(popupElement as Element, target.firstChild);
    this.hasDetached = true;
  },
  beforeDestroy() {
    const self: any = this;

    /**
     * Fixes bug where if popup was destroyed before the closing animation it would make it
     * impossible to scroll the page as overflow on the root element would still be hidden
     */
    if (this.value) {
      this.enableBodyScroll();
    }

    window.removeEventListener('animationend', this.dialogAnimationEnd);
    window.removeEventListener('keydown', this.windowKeyDown);

    if (!this.$refs.content) {
      return;
    }

    // IE11 Fix
    try {
      self.$refs.content.parentNode.removeChild(this.$refs.content);
    } catch (e) {
      console.log(e);
    }
  },
  methods: {
    popupMouseDown(e) {
      const { target } = e;
      const dialog = target.closest('.s-popup__dialog');
      if (dialog === null) {
        this.mouseDownInsideDialog = false;
        return;
      }

      this.mouseDownInsideDialog = true;
    },
    /**
     * Handles closing the popup when the user clicks outside of it
     */
    popupClick(e) {
      const {
        mouseDownInsideDialog, disableClickOutside, persistent, $refs,
      } = this;
      const popupElement = ($refs.popup as HTMLElement);
      const clickedInsidePopup = !(e.target === popupElement);

      // Don't close
      if (clickedInsidePopup || disableClickOutside || persistent || mouseDownInsideDialog) return;

      /**
       * This stops the modal closing if a user has an open vuetify select /
       * autocomplete component inside of it. Users may want to click outside the modal to close
       * the dropdown
       */
      if (popupElement.querySelector('.v-select--is-menu-active') !== null) return;

      // TODO: popupClick never actually fires when 'persistent' is set as per the
      // 'clickOutsideDeactivates' option on the focus trap
      // if (this.persistent) {
      //   this.wiggleClass = true;
      //   return;
      // }

      /**
       * Input event, closes the popup
       *
       * @event input
       * @type {boolean}
       */
      this.$emit('input', false);
    },
    windowKeyDown(e) {
      if (!this.value || e.key !== 'Escape') {
        return;
      }

      if (this.persistent) {
        this.wiggleClass = true;
        return;
      }

      // Close the modal
      this.$emit('input', false);
    },
    activateFocusTrap() {
      const self: any = this;

      self.focusTrap = createFocusTrap(self.$refs.dialog, {
        // In these 2 instances, clicking outside doesn't close the modal, so it shouldn't
        // deactivate the focus trap.
        clickOutsideDeactivates: !this.disableClickOutside && !this.persistent,

        // Force the main content to focus rather then the first focusable element (the close
        // button).
        initialFocus: '.s-popup__dialog__content',

        escapeDeactivates: !this.persistent,
      });
      self.focusTrap.activate();
      this.disableBodyScroll();
    },
    beforeLeave() {
      const self: any = this;
      if (self.focusTrap) {
        self.focusTrap.deactivate();
        self.focusTrap = null;
      }
      this.enableBodyScroll();
    },
    dialogAnimationEnd(e) {
      if (e.target !== this.$refs.dialog) {
        return;
      }
      this.wiggleClass = false;
    },
    disableBodyScroll() {
      documentElement.classList.add('prevent-scroll');
    },
    enableBodyScroll() {
      documentElement.classList.remove('prevent-scroll');
    },
    scrollToTop() {
      const content = this.$refs.modalBody as Element;
      content.scrollTop = 0;
    },
  },
});
